import React, { useState } from 'react';
import { Card, Button, Typography, Space, Select, Alert, message, Spin } from 'antd';
import { ApiOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const POSConnectionSetup = ({ onConnectionComplete, onCancel, defaultProvider = 'clover' }) => {
  // Helper function to handle authentication errors
  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.status === 204 || 
        (error.response?.status === 400)) {
      localStorage.removeItem('auth-token');
      message.error('Session expired. Please log in again.');
      window.location.href = '/login';
      return true;
    }
    return false;
  };

  const [provider, setProvider] = useState(defaultProvider);
  const [environment, setEnvironment] = useState('sandbox');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('setup'); // setup, connecting, success

  const providerInfo = {
    clover: {
      name: 'Clover POS',
      description: 'Connect your Clover POS system to import sales data, inventory, customers, and other business information.',
      icon: '🛒',
      dataTypes: ['Inventory items and categories', 'Sales orders and transactions', 'Customer information', 'Payment data', 'Business analytics']
    },
    square: {
      name: 'Square POS',
      description: 'Connect your Square POS system to import sales data, inventory, customers, and payment information.',
      icon: '⬜',
      dataTypes: ['Catalog items and categories', 'Orders and payments', 'Customer profiles', 'Location data', 'Transaction history']
    }
  };

  const handleConnect = async () => {
    try {
      setLoading(true);
      setStep('connecting');

      // Generate OAuth URL for the selected provider
      const response = await axios.post(`/pos-integrations/${provider}/auth-url`, {
        environment: environment
      }, {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': localStorage.getItem('auth-token')
        }
      });

      const result = response.data;
      
      if (result.success) {
        // Redirect to provider OAuth
        window.location.href = result.data.authUrl;
      } else {
        message.error(result.error || `Failed to initiate ${providerInfo[provider].name} connection`);
        setStep('setup');
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error(`Error connecting to ${provider}:`, error);
      message.error(`Failed to connect to ${providerInfo[provider].name}. Please try again.`);
      setStep('setup');
    } finally {
      setLoading(false);
    }
  };

  const renderSetupStep = () => (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>{providerInfo[provider].icon}</div>
          <Title level={3}>Connect to {providerInfo[provider].name}</Title>
          <Paragraph style={{ color: '#666', maxWidth: 500, margin: '0 auto' }}>
            {providerInfo[provider].description}
          </Paragraph>
        </div>

        <div style={{ maxWidth: 400, margin: '0 auto', width: '100%' }}>
          <div style={{ marginBottom: 24 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>POS Provider</Text>
            <Select
              value={provider}
              onChange={setProvider}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="clover">Clover POS</Option>
              <Option value="square">Square POS</Option>
            </Select>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Select your POS system provider
            </Text>
          </div>

          <div style={{ marginBottom: 24 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Environment</Text>
            <Select
              value={environment}
              onChange={setEnvironment}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="sandbox">Sandbox (Testing)</Option>
              <Option value="production">Production (Live)</Option>
            </Select>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Use sandbox for testing with demo data
            </Text>
          </div>

          <Alert
            message="Authorization Required"
            description={`You will be redirected to ${providerInfo[provider].name} to authorize this application. Please ensure you have a merchant account and the necessary permissions.`}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
{/* 
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>What data will be accessible:</Title>
            <ul style={{ color: '#666', paddingLeft: 20 }}>
              {providerInfo[provider].dataTypes.map((dataType, index) => (
                <li key={index}>{dataType}</li>
              ))}
            </ul>
          </div> */}

          <Space style={{ width: '100%', justifyContent: 'center' }}>
            <Button onClick={onCancel} size="large">
              Cancel
            </Button>
            <Button 
              type="primary" 
              size="large"
              onClick={handleConnect}
              loading={loading}
              icon={<ApiOutlined />}
            >
              Connect to {providerInfo[provider].name}
            </Button>
          </Space>
        </div>
      </Space>
    </Card>
  );

  const renderConnectingStep = () => (
    <Card>
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <Spin size="large" style={{ marginBottom: 24 }} />
        <Title level={4}>Connecting to {providerInfo[provider].name}...</Title>
        <Paragraph style={{ color: '#666' }}>
          You are being redirected to {providerInfo[provider].name} for authorization. 
          Please complete the login process and grant permissions.
        </Paragraph>
        <Alert
          message="If the redirect doesn't work"
          description="Please check that pop-ups are enabled for this site, or try refreshing the page."
          type="warning"
          showIcon
          style={{ maxWidth: 400, margin: '24px auto 0' }}
        />
      </div>
    </Card>
  );

  const renderSuccessStep = () => (
    <Card>
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
        <Title level={3}>Successfully Connected!</Title>
        <Paragraph style={{ color: '#666', marginBottom: 32 }}>
          Your {providerInfo[provider].name} system has been connected. You can now use this connection in your workflows.
        </Paragraph>
        <Button 
          type="primary" 
          size="large"
          onClick={onConnectionComplete}
        >
          Continue to Workflows
        </Button>
      </div>
    </Card>
  );

  // Check URL params for OAuth callback results
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const connectionId = urlParams.get('connectionId');

    // Check for any POS provider success
    if (success?.includes('_connected')) {
      const connectedProvider = success.split('_')[0];
      setProvider(connectedProvider);
      setStep('success');
      message.success(`${providerInfo[connectedProvider]?.name || connectedProvider} connection established successfully!`);
      
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      const errorMessage = urlParams.get('message') || 'Connection failed';
      const errorProvider = urlParams.get('provider') || provider;
      message.error(`${providerInfo[errorProvider]?.name || errorProvider} connection failed: ${decodeURIComponent(errorMessage)}`);
      setStep('setup');
      
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [provider]);

  switch (step) {
    case 'connecting':
      return renderConnectingStep();
    case 'success':
      return renderSuccessStep();
    default:
      return renderSetupStep();
  }
};

export default POSConnectionSetup;